import { cn } from '@simplywallst/ui-core/next';
import { CSS_CLASSES, hasValidImage } from './constants';
import { useImageLoading } from './hooks/useImageLoading';
import { useImageUrls } from './hooks/useImageUrls';
import { PlaceholderImage, MainImage } from './ImageComponents';

// NoImage component for when there's genuinely no image
export const NoImage = () => (
  <div
    role="img"
    aria-label="No image available"
    className={CSS_CLASSES.noImage}
  />
);

// Base image component that can be configured for blurred, normal, or content variants
export const BaseImage = ({
  src,
  alt,
  variant,
  containerClassName,
  priority = false,
}: {
  src: string;
  alt: string;
  variant: 'blurred' | 'normal' | 'content';
  containerClassName?: string;
  priority?: boolean;
}) => {
  const {
    isLoaded,
    showPlaceholder,
    hasError,
    imgRef,
    handleImageLoad,
    handleImageError,
  } = useImageLoading(priority);

  const { srcSet, placeholderUrl, mobileImageUrl } = useImageUrls(src, variant);

  const isValidImage = hasValidImage(src);

  if (!isValidImage || hasError) {
    return <NoImage />;
  }

  return (
    <div
      className={cn(
        CSS_CLASSES.container[variant],
        containerClassName,
        isLoaded ? 'opacity-100' : 'opacity-0'
      )}
    >
      {/* Placeholder image for progressive loading */}
      {showPlaceholder && (
        <PlaceholderImage src={placeholderUrl} variant={variant} />
      )}
      {/* Optimized image with progressive loading */}
      <MainImage
        src={mobileImageUrl}
        srcSet={srcSet}
        alt={alt}
        priority={priority}
        imgRef={imgRef}
        onLoad={handleImageLoad}
        onError={handleImageError}
        variant={variant}
      />
    </div>
  );
};

export const BlurredImage = ({
  src,
  alt,
  priority = false,
}: {
  src: string;
  alt: string;
  priority?: boolean;
}) => {
  return (
    <BaseImage src={src} alt={alt} priority={priority} variant="blurred" />
  );
};

export const NormalImage = ({
  src,
  alt,
  priority = false,
  containerClassName,
}: {
  src: string;
  alt: string;
  priority?: boolean;
  containerClassName?: string;
}) => {
  return (
    <BaseImage
      src={src}
      alt={alt}
      priority={priority}
      variant="normal"
      containerClassName={containerClassName}
    />
  );
};

export const ContentImage = ({
  src,
  alt,
  priority = false,
  containerClassName,
}: {
  src: string;
  alt: string;
  priority?: boolean;
  containerClassName?: string;
}) => {
  return (
    <BaseImage
      src={src}
      alt={alt}
      priority={priority}
      variant="content"
      containerClassName={containerClassName}
    />
  );
};

export const BlurredBackground = ({
  coverImage,
  alt,
  priority = false,
}: {
  alt: string;
  coverImage?: string;
  priority?: boolean;
}) => {
  if (!coverImage) return <NoImage />;

  return (
    <BaseImage
      src={coverImage}
      alt={alt}
      priority={priority}
      variant="blurred"
    />
  );
};

export const BlurredBackgroundWithProps = ({
  src,
  alt,
  priority = false,
}: {
  src: string;
  alt: string;
  priority?: boolean;
}) => {
  return (
    <BaseImage src={src} alt={alt} priority={priority} variant="blurred" />
  );
};

export const BackgroundImage = ({
  src,
  alt,
  priority = false,
  containerClassName,
}: {
  src: string;
  alt: string;
  priority?: boolean;
  containerClassName?: string;
}) => {
  return (
    <BaseImage
      src={src}
      alt={alt}
      priority={priority}
      variant="normal"
      containerClassName={containerClassName}
    />
  );
};
